// PACKAGES
import React, { useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import { Dropdown } from "semantic-ui-react";

// MATERIAL UI
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";

// COMPONENTS
import ReactLoader from "react-loader-spinner";
import UserFileUploader from "./UserFileUploader2";

// FILES
import api from "../../utils/api";
import useLoader from "../../hooks/useLoader";
import {displayError} from "../../utils/toastHelper";
import translator from "../../utils/translator";
import "./productCard.scss";

const ProductCard = (props) => {
    const {
        product,
        userFiles,
        updateUserFile,
        updateNonStock,
        fetchUserFiles,
        inventoryOn,
        hideModelNumber,
        hideStandardPricing,
        projectId,
        // From State
        roomIds,
        roomNames,
        selected_language,
    } = props;

	const selectedLanguage = selected_language || "en";
    const defaultRoomQuantity = Object.fromEntries(roomIds.map((id) => [id, "0"]));
    const isNonStock = !product.sku;

    const productInfo = isNonStock ? {
        name: product.non_stock_title,
        image: process.env.REACT_APP_PHP_API + "/images/_nonstock-images/" + product.non_stock_image,
        inventory: "N/A",
        sku: "",
        modelNumber: product.non_stock_model,
        internalName: product.non_stock_model,
    } : {
        name: product.name[selectedLanguage],
        image: userFiles.image
            ? process.env.REACT_APP_PHP_API + "/images/_user-product-images/" + userFiles.image
            : product.image || process.env.REACT_APP_PHP_API + "/images/LG_NotFound.jpg",
        specsManual: userFiles.spec
            ? process.env.REACT_APP_PHP_API + "/images/_user-product-specs/" + userFiles.spec
            : product.specsManual[selectedLanguage] || "",
        inventory: product.inventory || "-",
        sku: product.sku,
        modelNumber: product.modelNumber[selectedLanguage],
        internalName: product.internalName[selectedLanguage],
        description: product.description[selectedLanguage],
        bullets: product.bullets[selectedLanguage],
    };

    const [isLoading, load] = useLoader();
    const [updateDialogInfo, setUpdateDialogInfo] = useState({});
    const [productDialogOpen, setProductDialogOpen] = useState(false);
    const [price, setPrice] = useState("N/A");
    const [inWarehouse, setInWarehouse] = useState(false);
    const [roomQuantity, setRoomQuantity] = useState(defaultRoomQuantity);
    const [showStandardLabel, setShowStandardLabel] = useState(hideStandardPricing);

    const [title, setTitle] = useState(product.non_stock_title);
    const [model, setModel] = useState(product.non_stock_model);
    const [supplier, setSupplier] = useState(product.non_stock_supplier);
    const [itemGroup, setItemGroup] = useState(product.non_stock_item_group);
    const [itemCost, setItemCost] = useState(product.non_stock_item_cost);
    const [purchasePrice, setPurchasePrice] = useState(product.non_stock_purchase_price);

    const [validSupplier, setValidSupplier] = useState(null);
    const [supplierOptions, setSupplierOptions] = useState([]);
    const [supplierLoading, setSupplierLoading] = useState(false);
    const [validItemGroup, setValidItemGroup] = useState(null);
    const [itemGroupOptions, setItemGroupOptions] = useState([]);
    const [itemGroupLoading, setItemGroupLoading] = useState(false);

    const supplierTimeout = useRef(null);
    const itemGroupTimeout = useRef(null);
    const userModifiedItemGroupRef = useRef(false);

    useEffect(() => {
        return () => {
            if (supplierTimeout.current) clearTimeout(supplierTimeout.current);
            if (itemGroupTimeout.current) clearTimeout(itemGroupTimeout.current);
        };
    }, []);

    const initializedRef = useRef({ supplier: null, itemGroup: null });
    useEffect(() => {
        if (!(isNonStock && productDialogOpen)) return;
        console.log('[ProductCard2] Dialog open for non-stock product. supplier=', supplier, 'itemGroup=', itemGroup);
        if (supplier && initializedRef.current.supplier !== supplier) {
            console.log('[ProductCard2] Initialize supplier options with current supplier');
            setSupplierOptions([{ key: supplier, text: supplier, value: supplier }]);
            initializedRef.current.supplier = supplier;
            fetchItemGroupSuggestions('', supplier, itemGroup);
        }
        if (itemGroup && initializedRef.current.itemGroup !== itemGroup) {
            console.log('[ProductCard2] Initialize itemGroup option with current value');
            setItemGroupOptions(opts => {
                const exists = opts.some(o => o.value === itemGroup);
                return exists ? opts : [{ key: itemGroup, text: itemGroup, value: itemGroup }, ...opts];
            });
            initializedRef.current.itemGroup = itemGroup;
        }
    }, [productDialogOpen, isNonStock, supplier, itemGroup]);

    // Guard against race conditions with an incrementing request id
    const itemGroupRequestId = useRef(0);
    const fetchItemGroupSuggestions = async (searchQuery, supplierNo, currentValueForInjection) => {
        if (!supplierNo) { 
            console.log('[ProductCard2] fetchItemGroupSuggestions called without supplier, clearing options');
            setItemGroupOptions([]); 
            return; 
        }
        const reqId = ++itemGroupRequestId.current;
        console.log('[ProductCard2] fetchItemGroupSuggestions START', { reqId, searchQuery, supplierNo, currentValueForInjection, currentStateValue: itemGroup });
        setItemGroupLoading(true);
        try {
            const response = await api.post('/products/search-item-groups', {
                supplierNo,
                q: searchQuery || ''
            });
            const list = response?.listOfItemGroups || response || [];
            let options = list.map(item => ({
                key: String(item.itgr),
                text: item.description ? `${item.itgr} — ${item.description}` : String(item.itgr),
                value: String(item.itgr),
            }));
            const currentVal = (currentValueForInjection || itemGroup || '').trim();
            if (currentVal && !options.some(o => o.value === currentVal)) {
                console.log('[ProductCard2] Injecting current itemGroup into fetched options', currentVal);
                options = [{ key: currentVal, text: currentVal, value: currentVal }, ...options];
            }
            if (reqId !== itemGroupRequestId.current) {
                console.log('[ProductCard2] fetchItemGroupSuggestions ABORT (stale response)', { reqId, latest: itemGroupRequestId.current });
                return; // stale
            }
            setItemGroupOptions(options);
            console.log('[ProductCard2] fetchItemGroupSuggestions DONE', { reqId, optionCount: options.length });
            if ((!searchQuery || searchQuery === '') && list.length === 1 && !userModifiedItemGroupRef.current && !(itemGroup || currentVal)) {
                console.log('[ProductCard2] Auto-selecting sole backend item group (initial only)', list[0].itgr);
                setItemGroup(String(list[0].itgr));
                setValidItemGroup(true);
            } else if ((!searchQuery || searchQuery === '') && list.length === 1 && userModifiedItemGroupRef.current) {
                console.log('[ProductCard2] Suppressed auto-select because user modified itemGroup');
            }
        } catch (e) {
            if (reqId !== itemGroupRequestId.current) {
                console.log('[ProductCard2] fetchItemGroupSuggestions ERROR (stale) ignored', { reqId });
                return;
            }
            console.error('[ProductCard2] Failed to fetch item group suggestions', e);
            setItemGroupOptions(opts => {
                const currentVal = (currentValueForInjection || itemGroup || '').trim();
                if (currentVal && !opts.some(o => o.value === currentVal)) {
                    return [{ key: currentVal, text: currentVal, value: currentVal }];
                }
                return [];
            });
        } finally {
            if (reqId === itemGroupRequestId.current) {
                setItemGroupLoading(false);
            }
        }
    };

    const fetchSupplierSuggestions = async (searchQuery) => {
        if (!searchQuery) return;
        setSupplierLoading(true);
        try {
            const response = await api.post('/products/search-supplier', { query: searchQuery });
            const payload = response?.data ?? response;
            const list = payload?.listOfSuppliers ?? [];

            const options = list.map(item => ({
                key: item.SUNO,
                text: `${item.SUNM} (${item.SUNO})`,
                value: String(item.SUNO),
            }));

            setSupplierOptions(options);
        } catch (error) {
            console.error("Failed to fetch supplier suggestions", error);
            setSupplierOptions([]);
        } finally {
            setSupplierLoading(false);
        }
    };

    const fetchQuantities = async () => {
        try {
            const quantities = await api.post(
                "/products/room-product-quantity2",
                {
                    projectId,
                    sku: product?.sku,
                    nonStockIds: isNonStock && product.id.split(","),
                }
            );
            roomIds.forEach((roomId) => {
                if (!quantities[roomId]) quantities[roomId] = "0";
            });
            setRoomQuantity(quantities);
        } catch(error) {
            displayError(error, "Error fetching product information");
        }
    };

    const fetchProductInfo = async () => {
        try {
            // SKU Product
            if (product.sku) {
                const data = await api.post(
                    "/products/product-information2",
                    {
                        projectId,
                        sku: product.sku,
                    }
                );
                setPrice(data.price);
                setInWarehouse(data.inWarehouse);
            }
            // Non Stock Product
            else {
                setPrice(product.non_stock_price);
            }
        } catch(error) {
            displayError(error, "Error fetching product information");
        }
    };

    const checkIfStandard = async () => {
        try {
            if (!isNonStock && hideStandardPricing) {
                const standardData = await api.post(
                    "/standards/is-standard",
                    { project_id: projectId, sku: product.sku }
                );
                setShowStandardLabel(standardData.is_standard);
            }
        } catch(error) {
            displayError(error, "Error checking for standard product");
        }
    };

    const openDisplayCard = async () => {
        load(
            fetchProductInfo(),
            checkIfStandard(),
            fetchQuantities()
        );
        setProductDialogOpen(true);
    };

    const handleDisplayCardClose = () => {
        setProductDialogOpen(false);
    };

    const updateRoomQuantity = (roomId, value, allowEmpty) => {
        const update = (newValue) => setRoomQuantity({
            ...roomQuantity,
            [roomId]: newValue,
        });
        if (allowEmpty && value === "") update("");
        else update("" + (parseInt(value) || 0));
    };

    const saveChanges = async () => {
        try {
            setUpdateDialogInfo({
                open: true,
                isLoading: true,
                title: "Loading..."
            });

            if (isNonStock) {
                if (!validSupplier) {
                    const isValid = await api.post(
                        "/products/validate-supplier",
                        { supplier }
                    );
                    if (!isValid) {
                        setUpdateDialogInfo({
                            title: "Error",
                            content: "Invalid Supplier",
                        });
                        return;
                    } else {
                        setValidSupplier(true);
                    }
                }

                if (!validItemGroup) {
                    const isValid = await api.post(
                        "/products/validate-item-group",
                        { itemGroup }
                    );
                    if (!isValid) {
                        setUpdateDialogInfo({
                            title: "Error",
                            content: "Invalid Item Group",
                        });
                        return;
                    } else {
                        setValidItemGroup(true);
                    }
                }
            }

            const data = await api.post(
                "/products/update-product-quantity",
                {
                    projectId,
                    product,
                    quantities: roomQuantity,
                }
            );
            await fetchUserFiles();
            setUpdateDialogInfo({
                title: "Success",
                content: translator("Product Shopping", "update_product_quantity_success"),
            });
        } catch(error) {
            setUpdateDialogInfo({
                title: "Error",
                content: "Error updating product quantity",
            });
        }
    }

    const saveNonStock = async () => {
        try {
            setUpdateDialogInfo({
                open: true,
                isLoading: true,
                title: "Loading..."
            });

            if (!validSupplier) {
				const isValid = await api.post(
					"/products/validate-supplier",
					{ supplier }
				);
				if (!isValid) {
					setUpdateDialogInfo({
                        title: "Error",
                        content: "Invalid Supplier",
                    });
					return;
				} else {
					setValidSupplier(true);
				}
			}

			if (!validItemGroup) {
				const isValid = await api.post(
					"/products/validate-item-group",
					{ itemGroup }
				);
				if (!isValid) {
					setUpdateDialogInfo({
                        title: "Error",
                        content: "Invalid Item Group",
                    });
					return;
				} else {
					setValidItemGroup(true);
				}
			}

            const data = await api.post(
                "/products/update-non-stock-pricing",
                {
                    old_non_stock_title: product.non_stock_title,
                    old_non_stock_model: product.non_stock_model,
                    non_stock_title: title,
                    non_stock_model: model,
                    non_stock_supplier: supplier,
                    non_stock_item_group: itemGroup,
                    non_stock_price: price,
                    non_stock_item_cost: itemCost,
                    non_stock_purchase_price: purchasePrice,
                }
            );
            setUpdateDialogInfo({
                title: "Success",
                content: translator("Misc", "upload_files_success"),
            });
        } catch(error) {
            setUpdateDialogInfo({
                title: "Error",
                content: "Error updating ECI",
            });
        }
    };

    const DisplayCard = (
        <Button
            style={{ padding: "0" }}
            variant="outlined"
            color="primary"
            onClick={openDisplayCard}
        >
            <div class="card" style={{ width: "18rem" }}>
                <div class="product_card_img_wrapper">
                    <img
                        class="card-img-top"
                        src={productInfo.image}
                        alt="Card image cap"
                    />
                </div>
                <div class="card-body">
                    {/* NAME */}
                    <h5 class="card-title">
                        {productInfo.name}
                    </h5>
                    {/* INVENTORY */}
                    {inventoryOn && (
                        <p class="product-card-available">
                            Available: {productInfo.inventory}
                        </p>
                    )}
                    {/* SKU */}
                    {!isNonStock && (
                        <p class="card-text">{productInfo.sku}</p>
                    )}
                    {/* MODEL NUMBER */}
                    {!isNonStock && !hideModelNumber && productInfo.modelNumber && (
                        <p class="card-text">{productInfo.modelNumber}</p>
                    )}
                    {/* Internal Name */}
                    <p class="card-text">{productInfo.internalName}</p>
                </div>
            </div>
        </Button>
    );

    const SkuContent = !isNonStock && (
        <DialogContentText
            style={{ display: "grid", gridTemplateColumns: "1fr 3fr auto" }}
        >
            {userFiles?.id
                ? (
                    <UserFileUploader
                        isNonStock={false}
                        projectId={projectId}
                        recordId={userFiles.id}
                        vendorImage={product.image}
                        vendorSpec={product.specsManual[selectedLanguage]}
                        userImage={userFiles.image}
                        userSpec={userFiles.spec}
                        updateImage={(file) => updateUserFile(product.sku, "image", file)}
                        updateSpec={(file) => updateUserFile(product.sku, "spec", file)}
                    />
                ) : (
                    <img
                        class="productImage"
                        src={productInfo.image}
                        alt="product"
                    />
                )
            }
            <div class="productInformation">
                {/* NAME */}
                <div>{productInfo.name}</div>
                {/* MODEL NUMBER */}
                {!hideModelNumber && productInfo.modelNumber && (<div>{productInfo.modelNumber}</div>)}
                {/* PRICE */}
                <div>
                    {showStandardLabel
                        ? translator("Quote Print", "standard_label")
                        : "$" + price
                    }
                </div>
                {/* DESCRIPTION LABEL */}
                <div><b>{translator("Product Shopping", "description_label")}:</b></div>
                {/* DESCRIPTION */}
                <div>{productInfo.description}</div>
                {/* BULLET POINTS */}
                <ul>
                    {productInfo.bullets.map((bullet) => (<li>{bullet}</li>))}
                </ul>
                {/* NOT IN WAREHOUSE LABEL */}
                {!isNonStock && !inWarehouse && (
                    <div><b>{translator("Product Shopping", "not_in_warehouse")}:</b></div>
                )}
                {/* SPECS MANUAL */}
                {productInfo.specsManual && (
                    <a
                        class="notwhitelink"
                        target="_blank"
                        href={productInfo.specsManual}
                    >
                        {translator("Product Shopping", "details_dialog_brochure_label")}
                    </a>
                )}
            </div>
            <Button
                style={{ height: "3em", width: "0em" }}
                id="set-price-close-button"
                onClick={handleDisplayCardClose}
                color="primary"
            >
                <i class="fa fa-window-close fa-2x" aria-hidden="true" />
            </Button>
        </DialogContentText>
    );

    const NonStockContent = isNonStock && (
        <DialogContentText
            style={{ display: "grid", gridTemplateColumns: "1fr 3fr auto" }}
        >
            <UserFileUploader
                isNonStock={true}
                projectId={projectId}
                recordId={product.id.split(",")[0]}
                vendorImage={null}
                vendorSpec={null}
                userImage={product.non_stock_image}
                userSpec={product.non_stock_spec}
                updateImage={updateNonStock}
                updateSpec={updateNonStock}
            />
            <div class="productInformation">
                <div class="form-group">
                    <div class="form-group project-copy-form-group">
                        <label>{translator("Quote Document", "description_heading")}</label>
                        <input
                            type="text"
                            class="form-control  project-copy-input"
                            maxLength={48}
                            value={title}
                            onInput={event => setTitle(event.target.value)}
                        />
                    </div>
                    <div class="form-group project-copy-form-group">
                        <label>{translator("Quote Document", "model_heading")}</label>
                        <input
                            type="text"
                            class="form-control  project-copy-input"
                            value={model}
                            onInput={event => setModel(event.target.value)}
                        />
                    </div>
                    <div class="form-group project-copy-form-group">
                        <label>{translator("Quote Document", "supplier_heading")}</label>
                        <Dropdown
                            placeholder={translator("Quote Document", "supplier_heading")}
                            fluid
                            search
                            selection
                            options={supplierOptions}
                            value={supplier}
                            onSearchChange={(e, { searchQuery }) => {
                                if (supplierTimeout.current) clearTimeout(supplierTimeout.current);
                                supplierTimeout.current = setTimeout(() => {
                                    fetchSupplierSuggestions(searchQuery);
                                }, 300);
                            }}
                            onChange={(e, { value }) => {
                                setSupplier(value);
                                setValidSupplier(null);
                                setItemGroup('');
                                setItemGroupOptions([]);
                                setValidItemGroup(null);

                                if (supplierTimeout.current) clearTimeout(supplierTimeout.current);
                                supplierTimeout.current = setTimeout(async () => {
                                    if (value === "") {
                                        setValidSupplier(null);
                                        return;
                                    }
                                    try {
                                        const isValid = await api.post("/products/validate-supplier", { supplier: value });
                                        setValidSupplier(!!isValid);
                                        if (isValid) {
                                            fetchItemGroupSuggestions('', value);
                                        }
                                    } catch (e) {
                                        setValidSupplier(false);
                                    }
                                }, 500);
                            }}
                            loading={supplierLoading}
                            className={"form-control project-copy-input " + (validSupplier === true ? "validation-success" : validSupplier === false ? "validation-error" : "")}
                        />
                        {validSupplier === false ? <div className="validation-message">Invalid supplier</div> : null}
                    </div>
                    <div class="form-group project-copy-form-group">
                        <label>{translator("Quote Document", "item_group_heading")}</label>
                        <Dropdown
                            placeholder={translator("Quote Document", "item_group_heading")}
                            fluid
                            search
                            selection
                            disabled={!supplier}
                            options={itemGroupOptions}
                            value={itemGroup || ''}
                            onSearchChange={(e, { searchQuery }) => {
                                console.log('[ProductCard2] ItemGroup onSearchChange', searchQuery);
                                if (itemGroupTimeout.current) clearTimeout(itemGroupTimeout.current);
                                itemGroupTimeout.current = setTimeout(() => {
                                    fetchItemGroupSuggestions(searchQuery, supplier, itemGroup);
                                }, 300);
                            }}
                            onChange={(e, { value }) => {
                                console.log('[ProductCard2] ItemGroup onChange', value);
                                userModifiedItemGroupRef.current = true;
                                setItemGroup(String(value));
                                setValidItemGroup(null);
                                if (itemGroupTimeout.current) clearTimeout(itemGroupTimeout.current);
                                itemGroupTimeout.current = setTimeout(async () => {
                                    if (value === "") {
                                        setValidItemGroup(null);
                                        return;
                                    }
                                    try {
                                        const isValid = await api.post("/products/validate-item-group", { itemGroup: value });
                                        console.log('[ProductCard2] Validation result itemGroup=', value, 'valid=', !!isValid);
                                        setValidItemGroup(!!isValid);
                                        setItemGroupOptions(opts => {
                                            if (!opts.some(o => o.value === String(value))) {
                                                return [{ key: String(value), text: String(value), value: String(value) }, ...opts];
                                            }
                                            return opts;
                                        });
                                    } catch (e) {
                                        console.log('[ProductCard2] Validation error for itemGroup', value, e);
                                        setValidItemGroup(false);
                                    }
                                }, 500);
                            }}
                            loading={itemGroupLoading}
                            className={"form-control project-copy-input " + (validItemGroup === true ? "validation-success" : validItemGroup === false ? "validation-error" : "")}
                        />
                        {validItemGroup === false ? <div className="validation-message">Invalid item group</div> : null}
                    </div>
                    <div class="form-group project-copy-form-group">
                        <label>{translator("Quote Document", "net_price")}</label>
                        <input
                            type="number"
                            class="form-control  project-copy-input"
                            value={price}
                            onInput={event => setPrice(event.target.value.replace("-", ""))}
                            onBlur={() => (price === "" ? setPrice("0") : null)}
                        />
                    </div>
                    <div class="form-group project-copy-form-group">
                        <label>{translator("Quote Document", "item_cost_heading")}</label>
                        <input
                            type="number"
                            class="form-control  project-copy-input"
                            value={itemCost}
                            onInput={event => setItemCost(event.target.value.replace("-", ""))}
                            onBlur={() => (itemCost === "" ? setItemCost("0") : null)}
                        />
                    </div>
                    <div class="form-group project-copy-form-group">
                        <label>{translator("Quote Document", "purchase_price_heading")}</label>
                        <input
                            type="number"
                            class="form-control  project-copy-input"
                            value={purchasePrice}
                            onInput={event => setPurchasePrice(event.target.value.replace("-", ""))}
                            onBlur={() => (purchasePrice === "" ? setPurchasePrice("0") : null)}
                        />
                    </div>
                </div>
                <Button onClick={saveNonStock} color="primary" variant="outlined">
                    {translator("Misc", "save_label")}
                </Button>
            </div>
            <Button
                style={{ height: "3em", width: "0em" }}
                id="set-price-close-button"
                onClick={handleDisplayCardClose}
                color="primary"
            >
                <i class="fa fa-window-close fa-2x" aria-hidden="true" />
            </Button>
        </DialogContentText>
    );

    const RoomQuantities = (
        <div className="available-rooms-flex-container">
            {roomIds 
                ? roomIds.map((roomId, index) => (
                    <div class="product-shopping-quantity-input-container">
                        <input
                            onChange={(event) => updateRoomQuantity(roomId, event.target.value, true)}
                            onBlur={(event) => updateRoomQuantity(roomId, event.target.value, false)}
                            type="number"
                            min="0"
                            step="1"
                            class="form-control product-shopping-quantity-input"
                            value={roomQuantity[roomId]}
                        />
                        <label class="form-check-label" for="materialUnchecked">
                            {roomNames[index]}
                        </label>
                    </div>
                ))
                : ""
            }
        </div>
    );

    const UpdateDialog = (
        <Dialog open={updateDialogInfo.title} aria-labelledby="form-dialog-title">
            <DialogTitle id="form-dialog-title set-price-dialog-header">
                {updateDialogInfo.title}
            </DialogTitle>
            <DialogContent
                style={{
                    paddingRight: "10em"
                }}
            >
                {updateDialogInfo.isLoading
                    ? (
                        <ReactLoader
                            style={{
                                marginLeft: "8em"
                            }}
                            type="Oval"
                            color="#00BFFF"
                            height={50}
                            width={50}
                        />
                    )
                    : updateDialogInfo.content
                }
            </DialogContent>
            {!updateDialogInfo.isLoading && (
                <DialogActions>
                    <Button
                        onClick={() => setUpdateDialogInfo({})}
                        color="primary"
                    >
                        Okay
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );

    const ProductDialog = (
        <Dialog
            open={productDialogOpen}
            onClose={handleDisplayCardClose}
            aria-labelledby="form-dialog-title"
        >
            {isLoading
                ? (
                    <DialogContent>
                        <ReactLoader
                            type="Oval"
                            color="#00BFFF"
                            height={50}
                            width={50}
                        />
                    </DialogContent>
                ) : (
                    <DialogContent>
                        {isNonStock ? (NonStockContent) : (SkuContent)}
                        <h6>{translator("Product Shopping", "available_rooms")}</h6>
                        {RoomQuantities}
                        <Button
                            style={{ marginLeft: "10em" }}
                            variant="outlined"
                            color="primary"
                            onClick={saveChanges}
                        >
                            {translator("Product Shopping", "add_btn")}
                        </Button>
                    </DialogContent>
                )
            }
            {UpdateDialog}
        </Dialog>
    );

    return (
        <div class="form-dialog-container">
            {DisplayCard}
            {ProductDialog}
        </div>
    );
};

const mapStateToProps = state => ({
	APIUrl: state.APIUrl,
	selected_language: state.selected_language,
	roomNames: state.roomNames,
	roomIds: state.roomIds,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCard);
